import React, { Component } from 'react';
import { Modal, Radio, Select, Button, message, Spin, Alert, Divider } from 'antd';
import { PlayCircleOutlined, UserOutlined, ProjectOutlined, TeamOutlined } from '@ant-design/icons';
import { getProjectsForTbt, getSubcontractorsForTbt, getScheduledSubcontractorsForTbt, runSelectiveTbt, getToolboxTalkDocuments } from '../../../../HttpRequests/toolboxTalks';

const { Option } = Select;

class SelectiveRunModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: false,
      loading: false,
      distributionType: 'all',
      selectedProject: null,
      selectedSubcontractors: [],
      projects: [],
      subcontractors: [],
      loadingProjects: false,
      loadingSubcontractors: false,
      running: false,
      documentSelection: 'random',
      selectedDocuments: [],
      documents: [],
      loadingDocuments: false,
    };
  }

  componentDidMount() {
    this.loadProjects();
    this.loadDocuments();
  }

  loadProjects = async () => {
    this.setState({ loadingProjects: true });
    try {
      const response = await getProjectsForTbt();
      this.setState({ projects: response.data.projects || [] });
    } catch (error) {
      console.error('Failed to load projects:', error);
      message.error('Failed to load projects');
    } finally {
      this.setState({ loadingProjects: false });
    }
  };

  loadSubcontractors = async (projectId = null, useScheduledOnly = false) => {
    this.setState({ loadingSubcontractors: true });
    try {
      let response;
      if (useScheduledOnly) {
        response = await getScheduledSubcontractorsForTbt();
      } else {
        response = await getSubcontractorsForTbt(projectId);
      }
      this.setState({ subcontractors: response.data.subcontractors || [] });
    } catch (error) {
      console.error('Failed to load subcontractors:', error);
      message.error('Failed to load subcontractors');
    } finally {
      this.setState({ loadingSubcontractors: false });
    }
  };

  loadDocuments = async () => {
    this.setState({ loadingDocuments: true });
    try {
      const response = await getToolboxTalkDocuments({ params: { active: true } });
      console.log('Documents API response:', response.data); // Debug log
      // The API returns data directly when no pagination, or in response.data.rows when paginated
      const documents = response.data.rows || response.data || [];
      this.setState({ documents });
    } catch (error) {
      console.error('Failed to load documents:', error);
      message.error('Failed to load documents');
    } finally {
      this.setState({ loadingDocuments: false });
    }
  };

  show = () => {
    this.setState({ visible: true });
    this.loadSubcontractors(null, true); // Load scheduled subcontractors initially for "all" option
  };

  hide = () => {
    this.setState({
      visible: false,
      distributionType: 'all',
      selectedProject: null,
      selectedSubcontractors: [],
      documentSelection: 'random',
      selectedDocuments: [],
    });
  };

  handleDistributionTypeChange = (e) => {
    const distributionType = e.target.value;
    this.setState({
      distributionType,
      selectedProject: null,
      selectedSubcontractors: [],
    });

    // Load appropriate data based on selection
    if (distributionType === 'all') {
      this.loadSubcontractors(null, true); // Load scheduled subcontractors for "all" option
    } else if (distributionType === 'specific') {
      this.loadSubcontractors(); // Load all subcontractors for manual selection
    }
  };

  handleProjectChange = (projectId) => {
    this.setState({ selectedProject: projectId });
    if (projectId) {
      this.loadSubcontractors(projectId);
    }
  };

  handleSubcontractorChange = (subcontractorIds) => {
    this.setState({ selectedSubcontractors: subcontractorIds });
  };

  handleDocumentSelectionChange = (e) => {
    const documentSelection = e.target.value;
    this.setState({
      documentSelection,
      selectedDocuments: [], // Reset selected documents when changing selection type
    });
  };

  handleDocumentChange = (documentIds) => {
    this.setState({ selectedDocuments: documentIds });
  };

  handleRunTbt = async () => {
    const { distributionType, selectedProject, selectedSubcontractors, documentSelection, selectedDocuments } = this.state;

    // Validation
    if (distributionType === 'project' && !selectedProject) {
      message.error('Please select a project');
      return;
    }

    if (distributionType === 'specific' && selectedSubcontractors.length === 0) {
      message.error('Please select at least one subcontractor');
      return;
    }

    if (documentSelection === 'specific' && selectedDocuments.length === 0) {
      message.error('Please select at least one document');
      return;
    }

    this.setState({ running: true });

    try {
      const options = { type: distributionType };

      if (distributionType === 'project') {
        options.project_id = selectedProject;
      } else if (distributionType === 'specific') {
        options.subcontractor_ids = selectedSubcontractors;
      }

      // Add document selection options
      options.document_selection = documentSelection;
      if (documentSelection === 'specific') {
        options.document_ids = selectedDocuments;
      }

      const response = await runSelectiveTbt(options);

      message.success(`TBT run created successfully! Sent to ${response.data.run.total_sent} recipients.`);

      // Call parent callback to refresh data
      if (this.props.onRunComplete) {
        this.props.onRunComplete(response.data.run);
      }

      this.hide();

    } catch (error) {
      console.error('Failed to run TBT:', error);
      const errorMessage = (error.response && error.response.data && error.response.data.message) || 'Failed to create TBT run';
      message.error(errorMessage);
    } finally {
      this.setState({ running: false });
    }
  };

  getRecipientCount = () => {
    const { distributionType, selectedSubcontractors, subcontractors } = this.state;

    if (distributionType === 'all') {
      return Array.isArray(subcontractors) ? subcontractors.length : 0;
    } else if (distributionType === 'project') {
      return Array.isArray(subcontractors) ? subcontractors.length : 0;
    } else if (distributionType === 'specific') {
      return Array.isArray(selectedSubcontractors) ? selectedSubcontractors.length : 0;
    }

    return 0;
  };

  render() {
    const {
      visible,
      loading,
      distributionType,
      selectedProject,
      selectedSubcontractors,
      projects,
      subcontractors,
      loadingProjects,
      loadingSubcontractors,
      running,
      documentSelection,
      selectedDocuments,
      documents,
      loadingDocuments,
    } = this.state;

    const recipientCount = this.getRecipientCount();

    return (
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <PlayCircleOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Run Tool Box Talk
          </div>
        }
        visible={visible}
        onCancel={this.hide}
        width={600}
        footer={[
          <Button key="cancel" onClick={this.hide} disabled={running}>
            Cancel
          </Button>,
          <Button
            key="run"
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={this.handleRunTbt}
            loading={running}
            disabled={running || 
              (distributionType === 'specific' && selectedSubcontractors.length === 0) ||
              (documentSelection === 'specific' && selectedDocuments.length === 0)
            }
          >
            {running ? 'Running...' : `Run Now (${recipientCount} recipients)`}
          </Button>,
        ]}
      >
        <div style={{ marginBottom: 24 }}>
          <Alert
            message="Choose Distribution Options"
            description="Select who should receive the Tool Box Talk. You can send to scheduled subcontractors (those booked for upcoming work), filter by project, or choose specific subcontractors."
            type="info"
            showIcon
            style={{ marginBottom: 20 }}
          />

          <div style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 12 }}>Distribution Type:</h4>
            <Radio.Group
              value={distributionType}
              onChange={this.handleDistributionTypeChange}
              style={{ width: '100%' }}
            >
              <Radio.Button value="all" style={{ marginBottom: 8, width: '100%', textAlign: 'left' }}>
                <TeamOutlined style={{ marginRight: 8 }} />
                Send to Scheduled Subcontractors
              </Radio.Button>
              <Radio.Button value="project" style={{ marginBottom: 8, width: '100%', textAlign: 'left' }}>
                <ProjectOutlined style={{ marginRight: 8 }} />
                Send to Specific Project
              </Radio.Button>
              <Radio.Button value="specific" style={{ width: '100%', textAlign: 'left' }}>
                <UserOutlined style={{ marginRight: 8 }} />
                Send to Specific Subcontractors
              </Radio.Button>
            </Radio.Group>
          </div>

          {distributionType === 'project' && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12 }}>Select Project:</h4>
              <Select
                placeholder="Choose a project..."
                style={{ width: '100%' }}
                value={selectedProject}
                onChange={this.handleProjectChange}
                loading={loadingProjects}
                showSearch
                filterOption={(input, option) => {
                  // Get the project data from the option props
                  const project = projects.find(p => p.id === option.value);
                  if (!project) return false;
                  
                  const searchText = `${project.number} ${project.client_name} ${project.client_site_name}`.toLowerCase();
                  return searchText.indexOf(input.toLowerCase()) >= 0;
                }}
              >
                {projects.map(project => (
                  <Option key={project.id} value={project.id}>
                    [{project.number}] {project.client_name} - {project.client_site_name}
                  </Option>
                ))}
              </Select>
              {selectedProject && (
                <div style={{ marginTop: 8, color: '#666' }}>
                  <Spin spinning={loadingSubcontractors} size="small" style={{ marginRight: 8 }} />
                  {loadingSubcontractors ? 'Loading subcontractors...' : `${Array.isArray(subcontractors) ? subcontractors.length : 0} subcontractors found`}
                </div>
              )}
            </div>
          )}

          {distributionType === 'specific' && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12 }}>Select Subcontractors:</h4>
              <Select
                mode="multiple"
                placeholder="Choose subcontractors..."
                style={{ width: '100%' }}
                value={selectedSubcontractors}
                onChange={this.handleSubcontractorChange}
                loading={loadingSubcontractors}
                showSearch
                filterOption={(input, option) => {
                  // Get the subcontractor data from the option props
                  const subcontractor = subcontractors.find(s => s.id === option.value);
                  if (!subcontractor) return false;
                  
                  const searchText = `${subcontractor.name} ${subcontractor.group || ''}`.toLowerCase();
                  return searchText.indexOf(input.toLowerCase()) >= 0;
                }}
                maxTagCount={3}
                maxTagTextLength={20}
              >
                {Array.isArray(subcontractors) ? subcontractors.map(subcontractor => (
                  <Option key={subcontractor.id} value={subcontractor.id}>
                    {subcontractor.name}
                    {subcontractor.group && ` (${subcontractor.group})`}
                  </Option>
                )) : []}
              </Select>
              <div style={{ marginTop: 8, color: '#666' }}>
                {selectedSubcontractors.length > 0
                  ? `${selectedSubcontractors.length} subcontractor(s) selected`
                  : 'Select at least one subcontractor'
                }
              </div>
            </div>
          )}

          {distributionType === 'all' && (
            <div style={{ marginBottom: 20 }}>
              <Spin spinning={loadingSubcontractors} size="small" style={{ marginRight: 8 }} />
              {loadingSubcontractors ? (
                'Loading scheduled subcontractors...'
              ) : (
                <Alert
                  message={`Ready to send to ${Array.isArray(subcontractors) ? subcontractors.length : 0} scheduled subcontractors`}
                  type="success"
                  showIcon
                />
              )}
            </div>
          )}

          <Divider />

          <div style={{ marginBottom: 20 }}>
            <h4 style={{ marginBottom: 12 }}>Document Selection:</h4>
            <Radio.Group
              value={documentSelection}
              onChange={this.handleDocumentSelectionChange}
              style={{ width: '100%' }}
            >
              <Radio.Button value="random" style={{ marginBottom: 8, width: '100%', textAlign: 'left' }}>
                <PlayCircleOutlined style={{ marginRight: 8 }} />
                Random Selection (System will choose documents automatically)
              </Radio.Button>
              <Radio.Button value="specific" style={{ width: '100%', textAlign: 'left' }}>
                <UserOutlined style={{ marginRight: 8 }} />
                Select Specific Documents
              </Radio.Button>
            </Radio.Group>
          </div>

          {documentSelection === 'specific' && (
            <div style={{ marginBottom: 20 }}>
              <h4 style={{ marginBottom: 12 }}>Select Documents:</h4>
              <Select
                mode="multiple"
                placeholder="Choose documents..."
                style={{ width: '100%' }}
                value={selectedDocuments}
                onChange={this.handleDocumentChange}
                loading={loadingDocuments}
                showSearch
                filterOption={(input, option) => {
                  // Get the document data from the option props
                  const document = documents.find(d => d.id === option.value);
                  if (!document) return false;
                  
                  const searchText = `${document.title} ${document.description || ''}`.toLowerCase();
                  return searchText.indexOf(input.toLowerCase()) >= 0;
                }}
                maxTagCount={3}
                maxTagTextLength={30}
              >
                {Array.isArray(documents) ? documents.map(document => (
                  <Option key={document.id} value={document.id}>
                    {document.title}
                    {document.description && ` - ${document.description.substring(0, 50)}${document.description.length > 50 ? '...' : ''}`}
                  </Option>
                )) : []}
              </Select>
              <div style={{ marginTop: 8, color: '#666' }}>
                {selectedDocuments.length > 0
                  ? `${selectedDocuments.length} document(s) selected`
                  : 'Select at least one document'
                }
              </div>
            </div>
          )}

          {documentSelection === 'random' && (
            <div style={{ marginBottom: 20 }}>
              <Alert
                message="Random document selection enabled"
                description="The system will automatically select documents based on your TBT settings."
                type="info"
                showIcon
              />
            </div>
          )}

          <Divider />

          <div style={{ fontSize: '12px', color: '#666' }}>
            <strong>Note:</strong> {documentSelection === 'random' 
              ? 'The system will automatically select random Tool Box Talk documents based on your settings and send them to the selected recipients via email and/or SMS.'
              : 'The selected documents will be sent to all chosen recipients via email and/or SMS.'
            }
          </div>
        </div>
      </Modal>
    );
  }
}

export default SelectiveRunModal;
